import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Printer, ExternalLink, Link as LinkIcon, ImageDown, CheckCircle2 } from "lucide-react";
import QRCode from "./QRCode";
import { Button } from "./ui/button";
import { SmsImportPanel } from "./SmsImportPanel";

/**
 * DistributePanel — the "how do I get customers to install this pass" tab.
 *
 * The old panel had one button ("Print A5 poster") that didn't actually work.
 * This one gives merchants everything they need to distribute the pass with
 * zero design skills:
 *
 * • **QR-only PNG** — for embedding in emails, receipts, menus, till stickers
 *   (transparent bg + a "with background" variant).
 * • **A5 poster** — front-facing floor/counter poster (open PosterModal).
 * • **Table tent** — small 3-up printable strip for cafés / bars, printed on
 *   a home printer (3 to a page).
 * • **A4 wall poster** — large-format A4 for shop-window / community-board.
 * • **Full-quality vector SVG** — for the merchant's own designer to drop
 *   into whatever collateral they build.
 * • **Copy link** — plain https URL for social bios, WhatsApp broadcasts.
 * • **Open live page** — preview what customers see when they scan.
 *
 * Every download uses the join link with a distinctive `?src=` UTM so the
 * analytics dashboard can show which distribution channel actually drives
 * installs.
 */
export function DistributePanel({ program, orgId, orgName, orgSlug, joinUrl, onEditPoster }) {
  const [sizeVariant, setSizeVariant] = useState("a5"); // a5 · a4 · tent

  const withSrc = (src) => `${joinUrl}${joinUrl.includes("?") ? "&" : "?"}src=${src}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success("Join link copied");
    } catch (_) {
      toast.error("Couldn't copy — long-press the link and copy it manually.");
    }
  };

  // Render the on-screen QR into an off-screen canvas so we can export a
  // pixel-perfect PNG (transparent bg for design use, white bg for print).
  const downloadPng = async ({ transparent = false, size = 1024, filename = "backpocket-qr.png", src = "download-png" } = {}) => {
    try {
      const QRCodeLib = (await import("qrcode")).default;
      const url = withSrc(src);
      const canvas = document.createElement("canvas");
      await QRCodeLib.toCanvas(canvas, url, {
        width: size,
        margin: transparent ? 0 : 4,
        color: {
          dark: "#0B0D10",
          light: transparent ? "#00000000" : "#FFFFFF",
        },
      });
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`QR downloaded — ${filename}`);
    } catch (e) {
      toast.error("QR download failed — try the Copy link button instead");
    }
  };

  const downloadSvg = async () => {
    try {
      const QRCodeLib = (await import("qrcode")).default;
      const url = withSrc("download-svg");
      const svgString = await QRCodeLib.toString(url, {
        type: "svg", margin: 4,
        color: { dark: "#0B0D10", light: "#FFFFFF" },
      });
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const dataUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "backpocket-qr.svg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(dataUrl), 5000);
      toast.success("QR SVG downloaded");
    } catch (e) {
      toast.error("SVG download failed");
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6" data-testid="pane-distribute">
      {/* Header: status + preview button */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Distribute</div>
          <h3 className="font-display font-bold text-lg mt-1">
            Get customers to install your pass.
          </h3>
        </div>
        {program.status === "live" && (
          <a href={joinUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full h-9 text-[13px]" data-testid="distribute-open-live">
              <ExternalLink size={13} className="mr-1.5" />Preview live
            </Button>
          </a>
        )}
      </div>

      {program.status !== "live" ? (
        <div className="rounded-2xl border-2 border-dashed border-[#E8E5DE] p-8 text-center">
          <p className="text-sm text-[#25282E]/60">Publish the program first, then this panel gives you the QR, printable posters, and every download you need to seed your regulars.</p>
        </div>
      ) : (
        <>
          {/* Row 1 — QR + join link summary */}
          <div className="flex items-start gap-5 pb-5 border-b border-[#F0EDE5]">
            <div className="p-3 rounded-2xl bg-white border-2 border-[#0B0D10] shrink-0">
              <QRCode value={joinUrl} size={150} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Customer join link</div>
              <div className="text-[13px] text-[#0B0D10] break-all mt-1 font-mono">{joinUrl}</div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button data-testid="distribute-copy-link" onClick={copyLink} variant="outline" className="rounded-full h-9 text-[13px]">
                  <Copy size={13} className="mr-1.5" />Copy link
                </Button>
              </div>
              <p className="text-[12px] text-[#25282E]/55 mt-3 leading-relaxed">
                iPhone scans this QR → Apple Wallet install slides up instantly. Non-iOS browsers land on a friendly join page. One link, works everywhere.
              </p>
            </div>
          </div>

          {/* Row 2 — download the QR image (for menus, receipts, till stickers) */}
          <div className="pt-5 pb-5 border-b border-[#F0EDE5]">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50 mb-3">Download the QR only</div>
            <p className="text-[12.5px] text-[#25282E]/65 mb-3 leading-relaxed">Great for menus, receipts, till stickers, social bios — anywhere you already have a design.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button data-testid="download-qr-transparent" onClick={() => downloadPng({ transparent: true, size: 1024, filename: `${program.slug}-qr-transparent.png`, src: "qr-transparent" })}
                className="rounded-xl border border-[#E8E5DE] hover:border-[#0B0D10] p-3 text-left transition-colors">
                <ImageDown size={17} className="text-[#0B0D10]/70 mb-1.5" />
                <div className="text-[12.5px] font-semibold">Transparent PNG</div>
                <div className="text-[10.5px] text-[#25282E]/50">1024×1024 · for designers</div>
              </button>
              <button data-testid="download-qr-white" onClick={() => downloadPng({ transparent: false, size: 1024, filename: `${program.slug}-qr-white.png`, src: "qr-white" })}
                className="rounded-xl border border-[#E8E5DE] hover:border-[#0B0D10] p-3 text-left transition-colors">
                <ImageDown size={17} className="text-[#0B0D10]/70 mb-1.5" />
                <div className="text-[12.5px] font-semibold">On white PNG</div>
                <div className="text-[10.5px] text-[#25282E]/50">1024×1024 · print ready</div>
              </button>
              <button data-testid="download-qr-small" onClick={() => downloadPng({ transparent: false, size: 320, filename: `${program.slug}-qr-small.png`, src: "qr-small" })}
                className="rounded-xl border border-[#E8E5DE] hover:border-[#0B0D10] p-3 text-left transition-colors">
                <ImageDown size={17} className="text-[#0B0D10]/70 mb-1.5" />
                <div className="text-[12.5px] font-semibold">Small PNG</div>
                <div className="text-[10.5px] text-[#25282E]/50">320×320 · email/receipt</div>
              </button>
              <button data-testid="download-qr-svg" onClick={downloadSvg}
                className="rounded-xl border border-[#E8E5DE] hover:border-[#0B0D10] p-3 text-left transition-colors">
                <ImageDown size={17} className="text-[#0B0D10]/70 mb-1.5" />
                <div className="text-[12.5px] font-semibold">Vector SVG</div>
                <div className="text-[10.5px] text-[#25282E]/50">Infinite scale</div>
              </button>
            </div>
          </div>

          {/* Row 3 — full poster (A5/A4/table-tent) */}
          <div className="pt-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50 mb-3">Ready-made poster</div>
            <p className="text-[12.5px] text-[#25282E]/65 mb-3 leading-relaxed">Fully-designed poster with your business name, the QR, and 3-step "how it works" instructions. Print at home on any inkjet.</p>
            <div className="flex flex-wrap gap-1.5 mb-3 p-1 rounded-full bg-[#F0EDE5] border border-[#E8E5DE] w-fit">
              {[
                { id: "a5", label: "A5 counter poster", hint: "half-page, sits on the till" },
                { id: "a4", label: "A4 wall poster", hint: "full-page, shop window" },
                { id: "tent", label: "Table tent (3-up)", hint: "3 to an A4, tent-fold" },
              ].map((v) => (
                <button key={v.id} data-testid={`poster-size-${v.id}`} onClick={() => setSizeVariant(v.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${sizeVariant === v.id ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#25282E]/60 hover:text-[#0B0D10]"}`}>
                  {sizeVariant === v.id && <CheckCircle2 size={11} strokeWidth={2.6} />}{v.label}
                </button>
              ))}
            </div>
            <Button data-testid="distribute-open-poster" onClick={() => onEditPoster(program, sizeVariant)}
              className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 font-semibold px-5 text-[13.5px]">
              <Printer size={14} className="mr-1.5" />Open printable poster
            </Button>
          </div>

          {/* Row 4 — SMS bulk import (seed regulars via text message) */}
          {orgId && <SmsImportPanel orgId={orgId} program={program} />}
        </>
      )}
    </div>
  );
}
